import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  TextField,
  Autocomplete,
  Alert,
  Box,
  Button as MuiButton,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Chip,
} from '@mui/material';
import { AddBox } from '@mui/icons-material';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import {
  FeedbackError,
  Input,
  Textarea,
  Button,
  CustomDatePicker,
  CustomTimePicker,
  ChipList,
  TranslucentContainer,
  AlertComponent,
  DropzoneFile,
} from '../../../../../components';
import * as LocationActions from '../../../../../core/location/action';
import * as VenuesActions from '../../../../../core/venues/action';
import { onDisplayAlert } from '../../../../../utils/alerts/alerts';
import { useIsMobile } from '../../../../../hooks/useIsMobile';
import RecurringEventCalendar from '../AddRecurringEvents/RecurringEventCalendar';
import './CreateRetailSpace.scss';

function FormContentRetailSpace({ onCreateRetailSpace }) {
  const initialValues = {
    name: '',
    description: '',
    street_address: '',
    fk_state: undefined,
    fk_city: undefined,
    contact_name: '',
    contact_email: '',
    contact_phone_number: '',
    total_spots_available: '',
    price_per_hour: '',
    hour_start: '',
    hour_end: '',
    cancellation_deadline_hours: 24,
    application_deadline_hours: 24,
    commercial_insurance_required: false,
    allow_upfront_payment: true,
    unlisted: false,
  };

  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const [activeStep, setActiveStep] = useState(0);
  const [selectedDates, setSelectedDates] = useState([]);
  const [retailSpacePicture, setRetailSpacePicture] = useState([]);

  const { currentVenue } = useSelector((store) => store.venues);
  const { states, cities } = useSelector((store) => store.location);
  const { user } = useSelector((store) => store.users);

  const steps = ['Select Available Dates', 'Retail Space Details', 'Review & Create'];

  useEffect(() => {
    dispatch(LocationActions.getStates());
    if (user?.id) {
      dispatch(VenuesActions.getVenueById(user.id));
    }
  }, [dispatch, user?.id]);

  const onChangeFiles = (value) => {
    setRetailSpacePicture(value);
  };

  const handleNext = (e) => {
    // Prevent any form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = (e) => {
    // Prevent any form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = (values) => {
    // Demo only - show success message
    onDisplayAlert({
      title: 'Demo Mode',
      description: 'This is a demo. In production, this would create the retail space.',
      type: 'info',
    });
    console.log('Retail Space Data:', {
      ...values,
      selectedDates,
      retailSpacePicture,
      venue: currentVenue,
    });
  };

  // Step 1: Date Selection
  const renderDateSelection = () => (
    <Grid container justifyContent="center" marginTop={4}>
      <Grid item xs={12} md={10} lg={8}>
        <TranslucentContainer>
          <Box sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight="bold" color="#EE7B57" marginBottom={3}>
              Select Available Dates
            </Typography>
            <Typography variant="body1" marginBottom={3}>
              Choose the dates when your retail space will be available for booking.
            </Typography>

            <RecurringEventCalendar
              selectedDates={selectedDates}
              onDatesChange={setSelectedDates}
              existingEvents={[]}
            />
          </Box>
        </TranslucentContainer>
      </Grid>
    </Grid>
  );

  // Step 2: Retail Space Details Form
  const renderDetailsForm = (values, errors, touched, setFieldValue) => (
    <Grid container justifyContent="center" marginTop={4}>
      <Grid item xs={12} md={10} lg={8}>
        <TranslucentContainer>
          <Box sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight="bold" color="#EE7B57" marginBottom={3}>
              Retail Space Details
            </Typography>

            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold" marginBottom={2}>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Input
                  label="Retail Space Name"
                  name="name"
                  placeholder="e.g., Downtown Market Space"
                  fullWidth
                />
                <FeedbackError name="name" errors={errors} touched={touched} />
              </Grid>

              <Grid item xs={12}>
                <Textarea
                  label="Description"
                  name="description"
                  placeholder="Describe your retail space..."
                  rows={4}
                />
                <FeedbackError name="description" errors={errors} touched={touched} />
              </Grid>

              <Grid item xs={12}>
                <Input
                  label="Street Address"
                  name="street_address"
                  placeholder="123 Main Street"
                  fullWidth
                />
                <FeedbackError name="street_address" errors={errors} touched={touched} />
              </Grid>

              {/* Location */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  id="fk_state"
                  name="fk_state"
                  placeholder="State"
                  options={states || []}
                  disablePortal
                  onChange={(e, value) => {
                    if (value) {
                      setFieldValue('fk_state', value.id);
                      dispatch(LocationActions.getCitiesByStateID(value.id));
                    } else {
                      setFieldValue('fk_state', undefined);
                      setFieldValue('fk_city', undefined);
                    }
                  }}
                  getOptionLabel={(option) => `${option.state_code} - ${option.name}`}
                  renderInput={(params) => <TextField {...params} label="State" />}
                  clearOnEscape
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '50px',
                      background: '#ffffff',
                      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                      minHeight: '50px',
                    },
                  }}
                />
                <FeedbackError name="fk_state" errors={errors} touched={touched} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  id="fk_city"
                  name="fk_city"
                  placeholder="City"
                  options={cities || []}
                  disablePortal
                  disabled={!values.fk_state}
                  onChange={(e, value) => {
                    if (value) {
                      setFieldValue('fk_city', value.id);
                    } else {
                      setFieldValue('fk_city', undefined);
                    }
                  }}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => <TextField {...params} label="City" />}
                  clearOnEscape
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '50px',
                      background: '#ffffff',
                      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                      minHeight: '50px',
                    },
                  }}
                />
                <FeedbackError name="fk_city" errors={errors} touched={touched} />
              </Grid>

              {/* Availability & Pricing */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold" marginTop={2} marginBottom={2}>
                  Availability & Pricing
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <CustomTimePicker
                  label="Daily Start Time"
                  name="hour_start"
                  value={values.hour_start}
                  setFieldValue={setFieldValue}
                />
                <FeedbackError name="hour_start" errors={errors} touched={touched} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <CustomTimePicker
                  label="Daily End Time"
                  name="hour_end"
                  value={values.hour_end}
                  setFieldValue={setFieldValue}
                />
                <FeedbackError name="hour_end" errors={errors} touched={touched} />
              </Grid>

              <Grid item xs={12} sm={5}>
                <Typography variant="caption">Total Spots Available (per hour)</Typography>
                <Input
                  name="total_spots_available"
                  type="number"
                  placeholder="e.g., 10"
                  fullWidth
                />
                <FeedbackError name="total_spots_available" errors={errors} touched={touched} />
              </Grid>

              {/* Spacer */}
              <Grid item xs={0} sm={2} />

              <Grid item xs={12} sm={5}>
                <Typography variant="caption">Price Per Hour ($)</Typography>
                <Input name="price_per_hour" type="number" placeholder="e.g., 30.00" fullWidth />
                <FeedbackError name="price_per_hour" errors={errors} touched={touched} />
              </Grid>

              {/* Contact Information */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold" marginTop={2} marginBottom={2}>
                  Contact Information
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Input label="Contact Name" name="contact_name" placeholder="John Doe" fullWidth />
                <FeedbackError name="contact_name" errors={errors} touched={touched} />
              </Grid>

              <Grid item xs={12} sm={5}>
                <Typography variant="caption">Contact Email</Typography>
                <Input
                  name="contact_email"
                  type="email"
                  placeholder="contact@example.com"
                  fullWidth
                />
                <FeedbackError name="contact_email" errors={errors} touched={touched} />
              </Grid>

              {/* Spacer */}
              <Grid item xs={0} sm={2} />

              <Grid item xs={12} sm={5}>
                <Typography variant="caption">Contact Phone</Typography>
                <Input name="contact_phone_number" placeholder="(555) 123-4567" fullWidth />
                <FeedbackError name="contact_phone_number" errors={errors} touched={touched} />
              </Grid>

              {/* Photo Upload */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold" marginTop={2} marginBottom={2}>
                  Photos
                </Typography>
                <DropzoneFile
                  files={retailSpacePicture}
                  onChangeFiles={onChangeFiles}
                  maxFile={5}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" alignItems="center">
                      <Checkbox
                        checked={values.commercial_insurance_required}
                        onChange={(e) =>
                          setFieldValue('commercial_insurance_required', e.target.checked)
                        }
                        name="commercial_insurance_required"
                      />
                      <Typography>Require Commercial Insurance</Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <Checkbox
                        checked={values.allow_upfront_payment}
                        onChange={(e) => setFieldValue('allow_upfront_payment', e.target.checked)}
                        name="allow_upfront_payment"
                      />
                      <Typography>Allow Upfront Payment</Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <Checkbox
                        checked={values.unlisted}
                        onChange={(e) => setFieldValue('unlisted', e.target.checked)}
                        name="unlisted"
                      />
                      <Typography>Unlisted (hidden from public search)</Typography>
                    </Box>
                  </Box>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </TranslucentContainer>
      </Grid>
    </Grid>
  );

  // Step 3: Review & Submit
  const renderReview = (formValues) => {
    // Debug: Log the values to console
    console.log('Review Form Values:', formValues);

    return (
      <Grid container justifyContent="center" marginTop={4}>
        <Grid item xs={12} md={10} lg={8}>
          <TranslucentContainer>
            <Box sx={{ p: 4 }}>
              <Typography variant="h5" fontWeight="bold" color="#EE7B57" marginBottom={3}>
                Review & Create
              </Typography>

              <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" marginBottom={2}>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Name:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {formValues.name || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Address:
                    </Typography>
                    <Typography variant="body1">{formValues.street_address || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Description:
                    </Typography>
                    <Typography variant="body1">{formValues.description || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" marginBottom={2}>
                  Availability
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Selected Dates ({selectedDates.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {selectedDates.slice(0, 5).map((date) => (
                        <Chip
                          key={date}
                          label={moment(date).format('MMM DD, YYYY')}
                          size="small"
                          color="primary"
                        />
                      ))}
                      {selectedDates.length > 5 && (
                        <Chip label={`+${selectedDates.length - 5} more`} size="small" />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Daily Hours:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {formValues.hour_start || 'Not set'} - {formValues.hour_end || 'Not set'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Spots Available:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {formValues.total_spots_available || 'Not set'} per hour
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" marginTop={1}>
                      Location:
                    </Typography>
                    <Typography variant="body1">
                      {formValues.street_address || 'Not set'}
                    </Typography>
                    {(formValues.fk_city || formValues.fk_state) && (
                      <Typography variant="body2" color="text.secondary">
                        {cities.find((c) => c.id === formValues.fk_city)?.name || ''}{' '}
                        {states.find((s) => s.id === formValues.fk_state)?.state_code || ''}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Paper>

              <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" marginBottom={2}>
                  Pricing & Settings
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Price Per Hour:
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="#EE7B57">
                      ${formValues.price_per_hour || '0.00'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" marginTop={1}>
                      Settings:
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1} marginTop={1}>
                      {formValues.commercial_insurance_required && (
                        <Chip
                          label="Commercial Insurance Required"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                      {formValues.allow_upfront_payment && (
                        <Chip
                          label="Upfront Payment Allowed"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                      {formValues.unlisted && (
                        <Chip label="Unlisted" size="small" color="default" variant="outlined" />
                      )}
                      {!formValues.commercial_insurance_required &&
                        !formValues.allow_upfront_payment &&
                        !formValues.unlisted && (
                          <Typography variant="body2" color="text.secondary">
                            No special settings enabled
                          </Typography>
                        )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" marginBottom={2}>
                  Contact Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Name:
                    </Typography>
                    <Typography variant="body1">{formValues.contact_name || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Email:
                    </Typography>
                    <Typography variant="body1">{formValues.contact_email || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Phone:
                    </Typography>
                    <Typography variant="body1">
                      {formValues.contact_phone_number || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Alert severity="info" sx={{ mb: 3 }}>
                <strong>Demo Mode:</strong> The submit button below will display a demo message. No
                data will be saved.
              </Alert>
            </Box>
          </TranslucentContainer>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box className="create-retail-space">
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
        enableReinitialize>
        {({ values, errors, touched, setFieldValue, isSubmitting }) => (
          <Form>
            {/* Stepper */}
            <Grid container justifyContent="center" marginTop={4}>
              <Grid item xs={12} md={10} lg={8}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Grid>
            </Grid>

            {/* Step Content - Only render current step */}
            <Grid container>
              {activeStep === 0 && renderDateSelection()}
              {activeStep === 1 && renderDetailsForm(values, errors, touched, setFieldValue)}
              {activeStep === 2 && renderReview(values)}
            </Grid>

            {/* Navigation Buttons */}
            <Grid container justifyContent="center" marginTop={4} marginBottom={8}>
              <Grid item xs={12} md={10} lg={8}>
                <Grid container justifyContent="space-between">
                  <Grid item>
                    {activeStep > 0 && (
                      <Button
                        type="button"
                        label="Back"
                        onClick={handleBack}
                        disabled={isSubmitting}
                      />
                    )}
                  </Grid>
                  <Grid item>
                    <Button
                      type={activeStep === steps.length - 1 ? 'submit' : 'button'}
                      label={activeStep === steps.length - 1 ? 'Create Retail Space' : 'Next'}
                      onClick={activeStep === steps.length - 1 ? undefined : handleNext}
                      disabled={(activeStep === 0 && selectedDates.length === 0) || isSubmitting}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
}

FormContentRetailSpace.propTypes = {
  onCreateRetailSpace: PropTypes.func.isRequired,
};

function CreateRetailSpace() {
  const handleCreateRetailSpace = (retailSpace) => {
    console.log('Creating retail space:', retailSpace);
  };

  return <FormContentRetailSpace onCreateRetailSpace={handleCreateRetailSpace} />;
}

export default CreateRetailSpace;
